import React, { useEffect, useState } from 'react';
import PropTypes, { checkPropTypes } from 'prop-types';
import {
  MenuButton,
  Popper,
  Avatar,
  CloseOnEscape,
  CloseOnClickAway,
  ProfilePopoutBody,
  ActionsList,
  MenuList,
  FlexGrid
} from '@sumtotal/react-foundation';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  postMessage,
  addIframeClickListeners,
  removeIframeClickListeners
} from '../../../modules/core/services/coreIFrameHelper';
import { GetProcessedNodeUrl } from '../../../utils/urlHelper';
import Config from '../../../config/Config';
import { ApplicationPaths } from '../../../authentication/AuthorizationConstants';
import useUserContext from '../../hooks/useUserContext';
import authService from '../../../authentication/AuthorizeService';
import getLogger from '../../../utils/logger';
import useHeaderContext from '../../hooks/useHeaderContext';
import useSettingsContext from '../../hooks/useSettingsContext';

const StyledPopper = styled(Popper)`
  z-index: 10000;
`;

const StyledMenuList = styled(MenuList)`
  width: 11.25rem;
  background-color: ${(props) => props.theme.variables.headerBarBackground};
  color: ${(props) => props.theme.variables.headerBarColor};
  padding-top: 0rem;
`;

const StyledFlexGrid = styled(FlexGrid)`
  width: 11.25rem;
  background-color: ${(props) => props.theme.variables.headerBarBackground};
  color: ${(props) => props.theme.variables.headerBarColor};
  padding-top: 0rem;
  li:nth-child(1) {
    margin-top: 0.5rem;
  }
`;

const MenuContainer = styled.div`
  display: flex;
`;

const ProfileMenuButton = styled(MenuButton)`
  &.Mui-disabled {
    background-color: transparent;
    color: ${(props) => props.theme.variables.headerBarColor};
    opacity: 0.5;
  }
  .avatar-border {
    border: 0.1875rem solid #f8b300;
  }
  &:hover {
    background-color: ${(props) =>
      props.ariaExpanded ? props.theme.variables.leftNavHeaderBackground : 'none'};
  }
  &:focus-visible {
    outline: -webkit-focus-ring-color auto 0.0625rem;
    outline-offset: -0.25rem;
  }
`;

const getSettingValueFromContext = (settingContext, settingKey) => {
  if (settingContext && settingContext.settings) {
    const settingObj = settingContext.settings.filter((setting) => setting.key === settingKey)[0];
    return settingObj.value;
  }
  return null;
};

/**
 * Profile Menu component for App Header.
 *
 */
function PofileMenu(props) {
  const { userData = defaultProps.userData, profileMenus = defaultProps.profileMenus } = props;
  checkPropTypes(PofileMenu.propTypes, props, 'prop', PofileMenu.name);
  const { t } = useTranslation(['TRANSLATIONS_RCORE_TOPNAV']);
  const [popperAnchor, setPopperAnchor] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEmulation, setIsEmulation] = useState(false);
  const [isGuestUser, setIsGuestUser] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [itemList, setItemList] = useState([]);
  const { user } = useUserContext();
  const disableProfileMenu = user.isEmulatedUser ? false : user.mustAnswerSecurityQuestions;
  const { settingsData } = useSettingsContext();
  let metricKey = '';
  const headerData = useHeaderContext();
  const open = Boolean(popperAnchor);
  let index;
  const directReportAriaLabel = t('RCORE_TOPNAV_PROFILE_DIRECT_REPORTS', {
    count: userData.directReporteesCount
  });
  metricKey = getSettingValueFromContext(settingsData, 'ELIXHRUI_METRIC_KEY_METRIC');

  const closeMenu = () => {
    setPopperAnchor(null);
    setIsMenuOpen(false);
    removeIframeClickListeners();
  };
  const menuButtonClick = (event) => {
    if (isMenuOpen) {
      closeMenu();
    } else {
      setPopperAnchor(event.currentTarget);
      setIsMenuOpen(true);
      if (headerData.headerContext.coreFrameLoaded) {
        setTimeout(() => {
          addIframeClickListeners(closeMenu);
        }, 100);
      }
      setTimeout(() => {
        const focusElement = document.getElementById('profileButton');
        if (focusElement) focusElement.focus();
      });
    }
  };

  const getEmulationFlag = async () => {
    setIsEmulation(await authService.isEmulationMode());
  };

  const getGuestUserFlag = async () => {
    setIsGuestUser(await authService.isGuestUser());
  };

  useEffect(() => {
    // Data required for actionlist
    const items = [];
    // Data required for ProfilePopoutBody
    const userDetails = {};

    if (userData != null) {
      if (userData.personInfo.imagePath === '') {
        userDetails.imagePath = '';
      } else {
        userDetails.imagePath = `${Config.domain}${userData.personInfo.imagePath}`;
      }
      userDetails.fullName = userData.personInfo.fullName;
      userDetails.jobTitle = userData.jobTitle;
      userDetails.showDonut = false;
      if (metricKey !== null && metricKey !== '') {
        userDetails.showDonut = true;
      }
      if (userData.keyMeasure) {
        userDetails.value = Math.round(userData.keyMeasure.value);
        userDetails.keyMeasureKey =
          userData.keyMeasure.options && userData.keyMeasure.options.key
            ? userData.keyMeasure.options.key
            : '';
        userDetails.keyMeasureLabel = userData.keyMeasure.label ? userData.keyMeasure.label : '';
      } else {
        userDetails.value = 0;
        userDetails.keyMeasureLabel = '';
      }
      userDetails.donutVariant = getDonutColor(userDetails.keyMeasureKey, userDetails.value);
      setUserInfo(userDetails);
    }

    if (profileMenus != null) {
      profileMenus.forEach((menu) => {
        if (menu.nodeKey === 'Profile') {
          userDetails.showLink = true;
          userDetails.url = GetProcessedNodeUrl(menu);
          setUserInfo(userDetails);
        } else {
          const item = {
            key: menu.nodeKey,
            name: menu.name,
            url: menu.url,
            iconType: 'MaterialIcon',
            rpKey: menu.rpKey
          };
          if (menu.nodeKey === 'orgchart') {
            if (!(userData != null && userData.directReporteesCount > 0)) return;
            item.name += ` ${userData.directReporteesCount}`;
            item.iconName = 'People';
            item.actionType = 'OPEN_MINI_PROFILE_ORGCHART';
            item.ariaLabel = directReportAriaLabel;
          } else if (menu.nodeKey === 'self_myteam') {
            if (!(userData != null && userData.openRequisitionsCount > 0)) return;
            item.name += ` ${userData.openRequisitionsCount}`;
            item.iconName = 'AssignmentInd';
            item.actionType = 'NAVIGATE';
            item.url = GetProcessedNodeUrl(menu);
            item.url += '?defaultMetricKey=openrequisitions';
          } else if (menu.nodeKey === 'self_personaldetails') {
            item.iconName = 'Settings';
            item.actionType = 'OPEN_PROFILE_DETAILS';
          } else if (menu.nodeKey === 'BADGES_LEVELS') {
            item.iconName = 'EmojiEvents';
            item.actionType = 'OPEN_BADGES_AND_LEVELS';
          } else if (menu.nodeKey === 'menu_signout') {
            if (menu.isEmulationEnabled) {
              return;
            }
            item.iconName = 'ExitToApp';
            item.actionType = 'NAVIGATE';
            item.url = ApplicationPaths.Logout;
          } else if (menu.nodeKey === 'self_signin') {
            item.iconName = 'ExitToApp';
            item.actionType = 'REDIRECT';
            item.url = menu.url.replace('$HOST_NAME', window.location.host);
          }
          items.push(item);
        }
      });
    }
    setItemList(items);
  }, [profileMenus, userData, directReportAriaLabel, metricKey]);

  useEffect(() => {
    getEmulationFlag();
    getGuestUserFlag();
  }, []);

  function getDonutColor(key, value) {
    if (key === 'performanceGoalMetrics') return 'Info';
    if (key !== 'positionFit') {
      if (value > 0 && value <= 25) return 'Danger';
      if (value > 25 && value <= 50) return 'Warning';
      if (value > 50 && value <= 75) return 'Info';
      if (value > 75) return 'Success';
    } else {
      if (value > 0 && value <= 50) return 'Danger';
      if (value > 50 && value <= 70) return 'Warning';
      if (value > 70 && value <= 89) return 'Info';
      if (value > 89) return 'Success';
    }
  }
  const handleAvatarClick = () => {
    const data = {
      type: 'mini',
      userId: window.staticData.userID
    };
    postMessage('SHOW_EMPLOYEE_PROFILE', data, null, 'profileMenu-button-id');
    closeMenu();
  };
  const handleItemClick = (key, action, url, rpKey) => {
    if (action !== 'NAVIGATE' && action !== 'REDIRECT') {
      const eventData = { nodeKey: key, action, url };
      postMessage(action, eventData, null, 'profileMenu-button-id');
    } else if (action === 'REDIRECT') {
      if (key === 'self_signin') {
        authService
          .removeAccessToken()
          .then(() => {
            window.location.assign(url);
            return null;
          })
          .catch((err) => {
            getLogger().error(err);
          });
      }
    }
    // Signout & Open Requisitions will be hanlded with Route component so skip any actions on it
    else if (key !== 'menu_signout' && key !== 'self_myteam') {
      const eventData = { nodeKey: key, rpKey, url };
      postMessage('RCORE_NAVIGATION', eventData);
    }
    closeMenu();
  };

  const handleUserNameClick = () => {
    closeMenu();
  };

  const handleMenuListKeyDown = (event) => {
    const menuItems = document.querySelectorAll('.UserProfileInfo');
    if (index === undefined) {
      index = 0;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
      const focusElement = document.getElementById('profileMenu-button-id');
      if (focusElement) focusElement.focus();
    } else {
      switch (event.keyCode) {
        case 40:
          if (index < menuItems.length - 1) {
            index++;
          } else if (index === menuItems.length - 1) {
            index = 0;
          }
          break;
        case 38:
          if (index > 0) {
            index--;
          } else if (index === 0) {
            index = menuItems.length - 1;
          }
          break;
        case 9:
          event.preventDefault();
          // Without event.preventDefault(), the browser's default tab behavior takes over
          if (event.shiftKey) {
            index = index > 0 ? index - 1 : menuItems.length - 1;
          } else {
            index = index < menuItems.length - 1 ? index + 1 : 0;
          }
          break;
        default:
      }
      if (menuItems[index].firstChild) {
        menuItems[index].firstChild.focus();
      }
    }
  };

  return (
    <CloseOnEscape close={isMenuOpen ? closeMenu : undefined}>
      <CloseOnClickAway close={isMenuOpen ? closeMenu : undefined}>
        <MenuContainer>
          <ProfileMenuButton
            endIcon="KeyboardArrowDown"
            menuOpenIcon="KeyboardArrowUp"
            iconType="MaterialIcon"
            isOpen={isMenuOpen}
            onClick={(e) => menuButtonClick(e)}
            disabled={disableProfileMenu}
            id="profileMenu-button-id"
            ariaLabel={t('RCORE_TOPNAV_MY_PROFILE')}
            ariaExpanded={isMenuOpen}
            ariaHasPopUp
          >
            <Avatar
              variant="circular"
              height={2}
              width={2}
              src={userInfo.imagePath}
              alt={userInfo.fullName}
              className={isEmulation ? 'avatar-border' : ''}
            />
          </ProfileMenuButton>
          <StyledPopper
            toggle={open}
            anchorEl={popperAnchor}
            placement="bottom-end"
            disableAutoAdjust={false}
          >
            <StyledFlexGrid onKeyDown={handleMenuListKeyDown}>
              <ProfilePopoutBody
                textLine1={userInfo.fullName}
                textLine2={userInfo.jobTitle}
                textLine3={t('RCORE_TOPNAV_USER_PROFILE_METRIC_INFORMATION', {
                  label: userInfo.keyMeasureLabel,
                  value: userInfo.value === undefined ? '0' : userInfo.value.toString()
                })}
                imagePath={userInfo.imagePath}
                url={userInfo.url}
                showLink={userInfo.showLink}
                isGuestUser={isGuestUser}
                onAvatarClick={handleAvatarClick}
                onUserNameClick={handleUserNameClick}
                ariaLabelForTextLine1={t('RCORE_TOPNAV_MINI_PROFILE', {
                  name: userInfo.fullName
                })}
                ariaLabelForTextLine2={t('RCORE_TOPNAV_USER_PROFILE', {
                  name: userInfo.fullName,
                  role: userInfo.jobTitle,
                  label: userInfo.keyMeasureLabel,
                  value: userInfo.value === undefined ? '0' : userInfo.value.toString()
                })}
                ariaLabelForTextLine3={t('RCORE_TOPNAV_USER_PROFILE_WITHOUT_METRIC', {
                  name: userInfo.fullName,
                  role: userInfo.jobTitle
                })}
                value={userInfo.value === undefined ? 0 : userInfo.value}
                donutVariant={userInfo.donutVariant}
                showDonut={userInfo.showDonut}
                className="UserProfileInfo"
              />
              <StyledMenuList>
                <ActionsList
                  itemList={itemList}
                  onItemClick={handleItemClick}
                  className="UserProfileInfo"
                />
              </StyledMenuList>
            </StyledFlexGrid>
          </StyledPopper>
        </MenuContainer>
      </CloseOnClickAway>
    </CloseOnEscape>
  );
}
// PropType check
PofileMenu.propTypes = {
  /**
   * user data
   */
  userData: PropTypes.oneOfType([PropTypes.object]),
  /**
   * Profile Menus
   */
  profileMenus: PropTypes.arrayOf(PropTypes.shape({}))
};

const defaultProps = {
  userData: undefined,
  profileMenus: undefined
};

export default PofileMenu;
